import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import type { CustomerUserOrganisation } from 'api';
import { useGetCustomerOrganisations } from 'api';

type OrganisationsContextProps = {
  organisationId: string;
  currentOrganisation: CustomerUserOrganisation;
  organisations: CustomerUserOrganisation[];
};

const OrganisationsContext = createContext({} as OrganisationsContextProps);

type Props = {
  children: ReactNode;
};

export const OrganisationsProvider = ({ children }: Props) => {
  const { data: organisationsResponse } = useGetCustomerOrganisations();

  const organisations = useMemo(
    () => organisationsResponse?.organisations ?? [],
    [organisationsResponse]
  );

  const { organisationId } = useParams();

  const currentOrganisation = useMemo(
    () =>
      organisations.find(
        (organisation) => organisation.id === Number(organisationId)
      ),
    [organisationId, organisations]
  );

  if (!organisationId || !currentOrganisation) {
    throw new Error('Missing organisation');
  }

  return (
    <OrganisationsContext.Provider
      value={{ currentOrganisation, organisations, organisationId }}
    >
      {children}
    </OrganisationsContext.Provider>
  );
};

export const useOrganisationsContext = () => useContext(OrganisationsContext);
